import { getConfig } from '@config/index'

import { IS_BROWSER, IS_DEVELOPMENT } from '@constants/globals'

const config = getConfig()

export interface PixelExperimentTracker {
  trackAssignment: (experimentName: string) => void
  generatePixelUrl: (options?: {
    isPreview?: boolean
    containerId?: string
    userId?: string
  }) => string | null
  getAssignedExperimentNames: () => string[]
}

/**
 * Server-side tracker for pixel experiments that need client-side assignment tracking
 */
export class PixelExperimentTrackerImpl implements PixelExperimentTracker {
  private readonly assignedExperiments: Set<string> = new Set()
  private readonly shouldTrack: boolean

  constructor(shouldTrack: boolean) {
    this.shouldTrack = shouldTrack
  }

  /**
   * Track an assigned pixel experiment during SSR
   */
  trackAssignment(experimentName: string): void {
    if (this.shouldTrack) {
      this.assignedExperiments.add(experimentName)
    }
  }

  /**
   * Get all tracked experiment names for pixel injection
   */
  getAssignedExperimentNames(): string[] {
    return [...this.assignedExperiments]
  }

  /**
   * Generate pixel URL for experiment assignment tracking
   */
  generatePixelUrl(options?: {
    isPreview?: boolean
    containerId?: string
    userId?: string
  }): string | null {
    const assignedExperiments = this.getAssignedExperimentNames()

    if (assignedExperiments.length === 0) {
      return null
    }

    const baseUrl = IS_DEVELOPMENT && IS_BROWSER ? window.location.origin : config.paths.basePath

    const url = new URL('/api/v1/treatments/assign', baseUrl)
    const params = new URLSearchParams()

    // Add experiment names as query parameter
    params.set('experiments', assignedExperiments.join(','))

    // Add nonce to prevent browser caching
    params.set('nonce', Date.now().toString())

    // Add container ID if provided
    if (options?.containerId) {
      params.set('cid', options.containerId)
    }

    // Add user ID if provided
    if (options?.userId) {
      params.set('uid', options.userId)
    }

    // Add preview mode if applicable
    if (options?.isPreview) {
      params.set('isPreview', 'true')
    }

    // We need to use safe encoding since React by default would use HTML-encoding for attributes
    url.searchParams.set('params', btoa(params.toString()))

    return url.toString()
  }
}
